import { Tab } from '@headlessui/react'
import { ExternallinkIcon } from '@ordr-as/icons'
import { Banner, LoadingWand } from '@ordr-as/umami-react'
import { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useGetTerminal } from '../../api/terminalApi'
import { useRequest } from '../../api/utils'
import Container from '../../components/layout/Container'
import config from '../../config'
import { SelectTabContext } from '../../context/SelectTabContext'
import TerminalDetailsTab from './TerminalDetailsTab'
import TerminalEventsTab from './terminalEventsTab/TerminalEventsTab'
import { TerminalDto, TerminalEventDtoNewStateEnum } from '../../../generated-api'

const TerminalDetailPage = () => {
    const { selectedTabIndex, setSelectedTabIndex } = useContext(SelectTabContext)
    const [terminal, setTerminal] = useState<TerminalDto>()
    const { externalId } = useParams()
    const getTerminal = useGetTerminal()

    const {
        run: callGetTerminal,
        loading: terminalLoading,
        error: terminalError,
    } = useRequest(() => getTerminal(externalId))

    useEffect(() => {
        callGetTerminal().then((terminal) => {
            terminal && setTerminal(terminal)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const tabs = [
        {
            label: 'Details',
            content: <TerminalDetailsTab terminal={terminal!} />,
        },
        {
            label: 'Terminal events',
            content: <TerminalEventsTab terminalExternalId={terminal?.externalId} />,
        },
    ]

    return (
        <>
            {terminalLoading ? (
                <div className="text-center flex h-screen">
                    <div className="m-auto">
                        <LoadingWand className="-mt-9 mx-auto" />
                    </div>
                </div>
            ) : terminal ? (
                <Container tabletWidth="full" desktopWidth="full" maxWidth="7xl" marginX="0" returnUrl="/terminals">
                    <div className="px-3">
                        <div className="flex items-center">
                            <h1 className="inline mr-3">Terminal details</h1>
                            <span className="sr-only">{terminal.latestState}</span>
                            <div
                                className={`rounded-full w-0 p-3 inline-block ${
                                    terminal.latestState === TerminalEventDtoNewStateEnum.OK
                                        ? 'bg-green-dark'
                                        : terminal.latestState === TerminalEventDtoNewStateEnum.MAINTENANCE ||
                                          terminal.latestState === TerminalEventDtoNewStateEnum.BUSY
                                        ? 'bg-yellow-light'
                                        : 'bg-orange'
                                }`}
                            />
                        </div>
                        <p>{externalId}</p>

                        {terminal.accountHolderCode && terminal.adyenGeneratedStoreReference && (
                            <a
                                href={`${config.customerOnboardingWebsiteUrl}/submerchants/${terminal.accountHolderCode}/stores/${terminal.adyenGeneratedStoreReference}`}
                                target="_blank"
                                rel="noreferrer"
                                className="py-1 pr-2"
                            >
                                <span className="underline mr-2">See store details</span>
                                <ExternallinkIcon className="text-xs inline align-baseline" />
                            </a>
                        )}

                        <Tab.Group
                            key={selectedTabIndex}
                            defaultIndex={selectedTabIndex}
                            onChange={(idx: number) => setSelectedTabIndex(idx)}
                        >
                            <Tab.List className="flex space-x-3 rounded-xl mt-4">
                                {tabs.map(({ label }, idx) => (
                                    <Tab
                                        key={idx}
                                        className={({ selected }: { selected: boolean }) =>
                                            `w-1/3 tablet:w-1/4 desktop:w-1/5 py-4 leading-5 font-medium rounded-t-lg  shadow-lg z-10
                                    ${
                                        selected
                                            ? 'bg-brand-white text-brand-black w-200 border-b-4 border-brand-primary'
                                            : 'hover:bg-purple-lightest bg-grey-medium hover:text-brand-black text-grey-dark opacity-80 hover:opacity-100'
                                    }`
                                        }
                                    >
                                        {label}
                                    </Tab>
                                ))}
                            </Tab.List>
                            <Tab.Panels className="relative z-10">
                                {Object.values(tabs).map(({ content }, idx) => (
                                    <Tab.Panel key={idx}>{content}</Tab.Panel>
                                ))}
                            </Tab.Panels>
                        </Tab.Group>
                    </div>
                </Container>
            ) : (
                <Container returnUrl="/terminals">
                    <Banner kind="error" onClose={() => null} stretch="parent">
                        <>{terminalError ?? 'Terminal not found'}</>
                    </Banner>
                </Container>
            )}
        </>
    )
}

export default TerminalDetailPage
